// ============================================
// 오링왕국 - 타입 정의
// ============================================

export interface ResourceState {
    money: number;
    population: number;
    happiness: number;
    turn: number;
    era: Era;
    season: Season;
    day: number; // 1-30 per season
}

export enum Era {
    POND = 'POND',           // 작은 연못 (시작)
    VILLAGE = 'VILLAGE',     // 오리 마을
    CITY = 'CITY',           // 오링 도시
    KINGDOM = 'KINGDOM',     // 오링 왕국
    EMPIRE = 'EMPIRE'        // 오링 제국
}

export enum Season {
    SPRING = 'SPRING',
    SUMMER = 'SUMMER',
    AUTUMN = 'AUTUMN',
    WINTER = 'WINTER'
}

export enum BuildingType {
    TENT = 'TENT',
    FARM = 'FARM',
    MINE = 'MINE',
    BARRACKS = 'BARRACKS',
    CASTLE = 'CASTLE',
    TEMPLE = 'TEMPLE',
    SCHOOL = 'SCHOOL',
    HOSPITAL = 'HOSPITAL',
    MARKET = 'MARKET',
    HARBOR = 'HARBOR',
    WONDER = 'WONDER'
}

export interface Building {
    id: BuildingType;
    name: string;
    description: string;
    baseCost: number;
    costMultiplier: number;
    incomePerTick: number;
    happinessBonus: number;
    populationBonus: number;
    defenseBonus: number;
    unlockEra: Era;
    icon: string;
    count: number;
    maxCount: number;
}

// ============================================
// 이벤트 시스템
// ============================================

export type EventCategory =
    | 'daily'        // 일상 이벤트
    | 'seasonal'     // 계절 이벤트
    | 'crisis'       // 위기 이벤트
    | 'opportunity'  // 기회 이벤트
    | 'chain'        // 체인 이벤트
    | 'character'    // 캐릭터 이벤트
    | 'milestone'    // 마일스톤 이벤트
    | 'ending';      // 엔딩 이벤트

export interface GameEventOption {
    id: string;
    text: string;
    moneyChange: number;
    popChange: number;
    happinessChange: number;
    defenseChange?: number;
    outcomeDescription: string;
    // 체인 이벤트 연결
    triggersChain?: string;        // 새 체인 시작
    continuesChain?: string;       // 다음 체인 단계
    endsChain?: boolean;           // 체인 종료
    // 특수 효과
    unlockBuilding?: BuildingType;
    unlockCharacter?: string;
    setFlag?: string;              // 게임 플래그 설정
    requiresFlag?: string;         // 필요한 플래그
    requiresBuilding?: BuildingType; // 필요한 건물
    // 엔딩 트리거
    triggersEnding?: EndingType;
}

export interface GameEvent {
    id: string;
    title: string;
    description: string;
    category: EventCategory;
    options: GameEventOption[];
    // 조건
    minEra?: Era;
    maxEra?: Era;
    minPopulation?: number;
    maxPopulation?: number;
    minMoney?: number;
    maxMoney?: number;
    minHappiness?: number;
    maxHappiness?: number;
    season?: Season;
    requiresFlag?: string;
    requiresBuilding?: BuildingType;
    requiresCharacter?: string;
    // 체인 정보
    chainId?: string;
    chainStep?: number;
    // 메타
    weight?: number;  // 발생 확률 가중치
    oneTime?: boolean; // 1회성 이벤트
    icon?: string;
}

// ============================================
// 체인 이벤트 시스템
// ============================================

export interface EventChain {
    id: string;
    name: string;
    description: string;
    totalSteps: number;
    currentStep: number;
    isActive: boolean;
    isCompleted: boolean;
    startedAt: number;  // 시작 턴
    branches: string[]; // 선택한 분기들
}

// ============================================
// 캐릭터 시스템
// ============================================

export interface Character {
    id: string;
    name: string;
    title: string;
    description: string;
    icon: string;
    relationship: number;  // -100 ~ 100
    isUnlocked: boolean;
    metAt?: number;  // 만난 턴
}

export type CharacterId =
    | 'sage_duck'      // 현자 오리
    | 'merchant_goose' // 상인 거위
    | 'fox_general'    // 여우 대장
    | 'migrant_bird'   // 철새 여행자
    | 'mystic_swan'    // 신비한 백조
    | 'pirate_pelican' // 해적 펠리컨
    | 'royal_peacock'  // 왕족 공작
    | 'shadow_crow'    // 그림자 까마귀
    | 'ancient_turtle' // 고대 거북
    | 'rebel_sparrow'; // 반란군 참새

// ============================================
// 엔딩 시스템
// ============================================

export enum EndingType {
    // 긍정적 엔딩
    CONQUEST = 'CONQUEST',           // 대륙 정복
    PEACE = 'PEACE',                 // 영원한 평화
    GOLDEN_DUCK = 'GOLDEN_DUCK',     // 황금 오리
    SKY_KINGDOM = 'SKY_KINGDOM',     // 하늘 왕국
    SEA_KINGDOM = 'SEA_KINGDOM',     // 심해 왕국
    LEGEND = 'LEGEND',               // 전설의 오리
    MERCHANT_EMPIRE = 'MERCHANT_EMPIRE', // 상인 제국

    // 중립적 엔딩
    MIGRATION = 'MIGRATION',         // 대이주
    FOX_ALLIANCE = 'FOX_ALLIANCE',   // 여우 동맹
    ASCENSION = 'ASCENSION',         // 승천

    // 부정적 엔딩
    EXTINCTION = 'EXTINCTION',       // 멸종
    REBELLION = 'REBELLION',         // 반란
    FOX_CONQUEST = 'FOX_CONQUEST',   // 여우에게 정복
    STARVATION = 'STARVATION',       // 기근
    PLAGUE = 'PLAGUE',               // 전염병

    // 숨겨진 엔딩
    TRUE_ENDING = 'TRUE_ENDING',     // 진엔딩
    SECRET_DUCK = 'SECRET_DUCK',     // 비밀 오리
    TIME_LOOP = 'TIME_LOOP'          // 시간 루프
}

export interface Ending {
    type: EndingType;
    name: string;
    description: string;
    epilogue: string;
    icon: string;
    isPositive: boolean;
    isSecret: boolean;
    requirements: string;
}

// ============================================
// 게임 상태
// ============================================

export interface GameState {
    resources: ResourceState;
    buildings: Record<BuildingType, Building>;
    characters: Record<CharacterId, Character>;
    activeChains: EventChain[];
    completedChains: string[];
    flags: Set<string>;
    triggeredEvents: Set<string>;
    statistics: GameStatistics;
    currentEnding: EndingType | null;
    isGameOver: boolean;
}

export interface GameStatistics {
    totalClicks: number;
    totalMoneyEarned: number;
    totalMoneySpent: number;
    totalPopulationGained: number;
    totalPopulationLost: number;
    eventsTriggered: number;
    chainsCompleted: number;
    buildingsBuilt: number;
    charactersUnlocked: number;
    highestPopulation: number;
    highestMoney: number;
    highestHappiness: number;
    crisesSurvived: number;
    playTime: number; // in seconds
}

export interface LogEntry {
    id: number;
    message: string;
    type: 'info' | 'success' | 'danger' | 'warning' | 'special';
    icon?: string;
}

// ============================================
// 업적 시스템
// ============================================

export interface Achievement {
    id: string;
    name: string;
    description: string;
    icon: string;
    isUnlocked: boolean;
    unlockedAt?: number;
    reward?: {
        money?: number;
        happiness?: number;
        population?: number;
    };
}

// ============================================
// 채굴 업그레이드 시스템
// ============================================

export enum MiningUpgradeType {
    GOLDEN_BEAK = 'GOLDEN_BEAK',         // 황금 부리 - 클릭당 +1
    DIVING_GOGGLES = 'DIVING_GOGGLES',   // 잠수 고글 - 클릭당 +2
    TREASURE_MAP = 'TREASURE_MAP',       // 보물 지도 - 클릭당 +5
    MAGIC_WAND = 'MAGIC_WAND',           // 마법 지팡이 - 클릭당 x2
    AUTO_FISHER = 'AUTO_FISHER',         // 자동 낚시꾼 - 초당 자동 채굴
    LUCKY_CLOVER = 'LUCKY_CLOVER',       // 행운의 클로버 - 10% 확률 2배
    DUCK_ARMY = 'DUCK_ARMY',             // 오리 군단 - 오토 클릭 부대
    GOLDEN_EGG = 'GOLDEN_EGG'            // 황금알 - 궁극기
}

export interface MiningUpgrade {
    id: MiningUpgradeType;
    name: string;
    description: string;
    icon: string;
    baseCost: number;
    costMultiplier: number;
    level: number;
    maxLevel: number;
    effect: {
        clickBonus?: number;      // 클릭당 추가 오링
        clickMultiplier?: number; // 클릭 배수
        autoMining?: number;      // 초당 자동 채굴량
        luckyChance?: number;     // 럭키 확률 (0-1)
    };
}

// ============================================
// 미니게임 시스템
// ============================================

export type MiniGameType = 'hidden_picture' | 'puzzle' | 'memory' | 'catch_duck';

export interface MiniGame {
    type: MiniGameType;
    name: string;
    description: string;
    reward: number;
    timeLimit: number; // 초
    difficulty: 1 | 2 | 3;
}

export interface HiddenPictureGame {
    targetEmoji: string;
    gridSize: number;
    targetCount: number;
    foundCount: number;
    grid: string[][];
    targetPositions: { x: number; y: number }[];
}

export interface PuzzleGame {
    pieces: number[];
    correctOrder: number[];
    moves: number;
    image: string;
}
